import { AlertColor } from '@mui/material';
import { DateRange } from 'react-day-picker';
import { Descendant } from 'slate';
import { LatLng, ReactionData, RolesMap, TripRole } from '../../firebase/firebase.types';

export enum TripCreationStatus {
  NOT_CREATED = 'trip/NOT_CREATED',
  CREATING = 'trip/CREATING',
  CREATED = 'trip/CREATED',
}

export const TripRolesAssignable = [TripRole.EDITOR, TripRole.VIEWER];

// Additive alongside Trip.roles — not a replacement. roles keeps being
// written/read everywhere until every read path migrates (separate ticket).
export interface TripMember {
  userId: string;
  role: TripRole;
  joinedAt: string;
  invitedBy?: string | null;
}

export interface Trip extends Record<string, unknown> {
  id: TripID;
  activeListId?: ListID; // ID of list currently selected in playground
  listOrder: ListID[];
  originalListOrder?: ListID[];
  location: LatLng;
  notes: Descendant[];
  public: boolean;
  roles: RolesMap;
  title: string;
  lists: TripList[];
  dividers: TripDivider[];
  cards: { [key: CardID]: TripCard };
  cardIdToListId: { [key: CardID]: ListID };
  startDateISO?: DateISO;
  endDateISO?: DateISO;
  days: { [key: DateISO]: TripDay };
  events: { [key: EventID]: TripEvent };
  cardIdToEventId: { [key: CardID]: EventID };
  createdAt: string | null;
  lastOpened?: { [userId: string]: string };
}

export interface CurrentTrip extends Trip {
  isChangedDefaultDates?: boolean;
  deletedEvents?: TripEvent[];
  mapCenter?: { lat: number; lng: number };
  mapZoom?: number;
  lastFetchedAt?: string;
}

export interface TripList extends Record<string, unknown> {
  id: ListID;
  emoji: string;
  color?: string;
  name: string;
  cardOrder?: CardID[];
  originalCardOrder?: CardID[];
  createdAt: string | null;
}

export interface TripDivider extends Record<string, unknown> {
  id: DividerID;
  emoji: string;
  name: string;
  listOrder: ListID[];
  originalListOrder?: ListID[];
  createdAt: string | null;
}

export type TopLevelItem =
  | { itemType: 'list'; data: TripList }
  | { itemType: 'divider'; data: TripDivider };

export interface TripCard extends Record<string, unknown> {
  id: CardID;
  addressComponents?: google.maps.GeocoderAddressComponent[];
  adrAddress?: string;
  businessStatus?: google.maps.places.BusinessStatus;
  formattedAddress?: string;
  formattedPhoneNumber?: string;
  googlePlaceId: string;
  internationalPhoneNumber?: string;
  location?: LatLng;
  name?: string;
  customName?: string;
  notes?: Descendant[];
  openingHours?: string[];
  priceLevel?: number;
  rating?: number;
  userRatingsTotal?: number;
  placeType?: string;
  types?: string[];
  url?: string;
  website?: string;
  creator: string;
  // Legacy: full Google photo URLs with an embedded API key (SEC-03/04). No longer
  // written for new or updated cards — see photoReferences. Left in place for
  // existing cards until the MIG-05 backfill; rendered as-is until then.
  imgUrls?: string[];
  // Opaque Places API photo references, resolved to renderable images at render
  // time via the getPlacePhoto gateway function. Never a fetchable URL itself.
  photoReferences?: string[];
  isInItinerary?: boolean;
  dateAdded: string | null;
  defaultImage?: number | null;
  reactions?: ReactionData;
  tags?: string[];
  lastOpened?: { [userId: string]: string };
}

export interface TripDay {
  id?: DayID; // undefined if not yet saved to DB
  dateISO: DateISO;
  notes?: Descendant[];
  eventOrder?: EventID[];
}

export interface TripEvent {
  id?: EventID; // undefined if not yet saved to DB
  cardId: CardID;
  startDateISO: DateISO;
  isConfirmed: boolean;
  endDateISO?: DateISO;
  eventLength?: number;
}

export type TripID = string;
export type ListID = string;
export type CardID = string;
export type DayID = string;
export type EventID = string;
export type DividerID = string;
export type DateISO = string; // e.g. 2022-12-25

export const isEventID = (item: EventID | undefined): item is EventID => Boolean(item);

export interface ISetDefaultDaysPayload {
  days: { [key: DateISO]: TripDay };
}

export interface IChangeEventLengthPayload {
  tripId: string;
  eventId: string;
  eventLength?: number;
}

export interface IChangeDefaultDatesThunkPayload {
  tripId: string;
  newStartDate: Date;
  defaultStartDate: Date;
  defaultNumDays: number;
}

export interface IChangeDefaultDatesServicePayload {
  tripId: string;
  enumDays: Record<string, string>;
}

export interface IChangeDefaultDatesThunkResult {
  startDateISO: string;
  endDateISO: string;
  days: { [key: DateISO]: TripDay };
  events: { [key: EventID]: TripEvent };
}

export interface TripDatesUpdateSuccess {
  tripId: TripID;
  startDateISO: DateISO;
  endDateISO: DateISO;
  days: { [key: DateISO]: TripDay };
  events: { [key: EventID]: TripEvent };
  deletedEvents: TripEvent[];
}

export interface TripDatesUpdateProps {
  tripId: TripID;
  oldDateRange?: DateRange;
  newDateRange: DateRange;
  days: { [key: DateISO]: TripDay };
}

export type SnackbarMessage = {
  severity: AlertColor;
  text: string;
};
